"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const CITIES = ["Paris", "Lyon", "Marseille", "Bordeaux"];
const SEED = [
    { id: 1, nom: "Camille", ville: "Paris" },
    { id: 2, nom: "Hugo", ville: "Lyon" },
    { id: 3, nom: "Inès", ville: "Paris" },
    { id: 4, nom: "Noah", ville: "Marseille" },
];

export default function PhpDatabase() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [members, setMembers] = useState(SEED);
    const [nextId, setNextId] = useState(5);
    const [nom, setNom] = useState("");
    const [ville, setVille] = useState("Paris");
    const [filter, setFilter] = useState("");
    const [sql, setSql] = useState(null); // { text, type }
    const [highlightId, setHighlightId] = useState(null);
    const hlTimer = useRef(null);

    useEffect(() => {
        return () => {
            if (hlTimer.current) clearTimeout(hlTimer.current);
        };
    }, []);

    function addMember(e) {
        e.preventDefault();
        const clean = nom.trim() || t("Php_db_anon");
        const row = { id: nextId, nom: clean, ville };
        setMembers((m) => [...m, row]);
        setNextId((n) => n + 1);
        setSql({
            text: `INSERT INTO membres (nom, ville) VALUES ("${clean}", "${ville}");`,
            type: "insert",
        });
        setNom("");
        setHighlightId(row.id);
        if (hlTimer.current) clearTimeout(hlTimer.current);
        hlTimer.current = setTimeout(() => setHighlightId(null), 1800);
    }

    function onFilter(v) {
        setFilter(v);
        setSql({
            text: v
                ? `SELECT * FROM membres WHERE ville = "${v}";`
                : "SELECT * FROM membres;",
            type: "select",
        });
    }

    const matchCount = members.filter(
        (m) => filter === "" || m.ville === filter,
    ).length;

    return (
        <section id="php-database" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Php_db_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Php_db_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:mysql"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        membres
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Php_db_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Contrôles : INSERT + SELECT */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <form
                            onSubmit={addMember}
                            className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-4"
                        >
                            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-300/90 mb-3">
                                <SolarIcon
                                    name="pen-new-square-bold"
                                    className="w-4 h-4"
                                />
                                {t("Php_db_insert_label")}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="text"
                                    value={nom}
                                    onChange={(e) => setNom(e.target.value)}
                                    placeholder={t("Php_db_nom_placeholder")}
                                    maxLength={16}
                                    className="flex-1 min-w-0 bg-white/[0.06] ring-1 ring-white/10 focus:ring-indigo-400/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 outline-hidden transition"
                                />
                                <select
                                    value={ville}
                                    onChange={(e) => setVille(e.target.value)}
                                    className="bg-white/[0.06] ring-1 ring-white/10 focus:ring-indigo-400/50 rounded-lg px-3 py-2 text-sm text-white outline-hidden transition"
                                >
                                    {CITIES.map((c) => (
                                        <option
                                            key={c}
                                            value={c}
                                            className="bg-bgDark-700"
                                        >
                                            {c}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="submit"
                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="add-circle-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Php_db_add_button")}
                                </button>
                            </div>
                        </form>

                        <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-4">
                            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-300/90 mb-3">
                                <SolarIcon
                                    name="magnifer-bold"
                                    className="w-4 h-4"
                                />
                                {t("Php_db_select_label")}
                            </div>
                            <div className="flex items-center gap-2">
                                <select
                                    value={filter}
                                    onChange={(e) => onFilter(e.target.value)}
                                    className="flex-1 min-w-0 bg-white/[0.06] ring-1 ring-white/10 focus:ring-indigo-400/50 rounded-lg px-3 py-2 text-sm text-white outline-hidden transition"
                                >
                                    <option value="" className="bg-bgDark-700">
                                        {t("Php_db_all_cities")}
                                    </option>
                                    {CITIES.map((c) => (
                                        <option
                                            key={c}
                                            value={c}
                                            className="bg-bgDark-700"
                                        >
                                            {c}
                                        </option>
                                    ))}
                                </select>
                                <span className="shrink-0 text-xs text-gray-400 tabular-nums">
                                    {t("Php_db_results", { count: matchCount })}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Console SQL */}
                    <div className="mt-4 flex items-center gap-2 rounded-xl bg-black/40 ring-1 ring-white/10 px-3 py-2.5 overflow-x-auto">
                        <SolarIcon
                            name="play-circle-bold"
                            className="w-4 h-4 shrink-0 text-emerald-400"
                        />
                        {sql ? (
                            <code className="text-[12.5px] font-mono whitespace-nowrap">
                                <span
                                    className={
                                        sql.type === "insert"
                                            ? "text-amber-300"
                                            : "text-sky-300"
                                    }
                                >
                                    {sql.text}
                                </span>
                            </code>
                        ) : (
                            <span className="text-[12.5px] font-mono text-gray-500">
                                {t("Php_db_console_hint")}
                            </span>
                        )}
                    </div>

                    {/* Table membres */}
                    <div className="mt-4 rounded-2xl bg-white/[0.02] ring-1 ring-white/10 overflow-hidden">
                        <div className="grid grid-cols-[3rem_1fr_1fr] gap-2 px-4 py-2.5 border-b border-white/10 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            <span>{t("Php_db_col_id")}</span>
                            <span>{t("Php_db_col_nom")}</span>
                            <span>{t("Php_db_col_ville")}</span>
                        </div>
                        <div className="divide-y divide-white/5">
                            <AnimatePresence initial={false}>
                                {members.map((m) => {
                                    const match =
                                        filter === "" || m.ville === filter;
                                    const isNew = m.id === highlightId;
                                    return (
                                        <motion.div
                                            key={m.id}
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0, height: 0 }
                                            }
                                            animate={{ opacity: 1, height: "auto" }}
                                            transition={{
                                                duration: reduce ? 0 : 0.3,
                                            }}
                                            className={`grid grid-cols-[3rem_1fr_1fr] gap-2 px-4 py-2.5 text-sm font-mono transition-colors ${
                                                isNew
                                                    ? "bg-indigo-500/15"
                                                    : match
                                                      ? ""
                                                      : "opacity-35"
                                            }`}
                                        >
                                            <span className="text-gray-500">
                                                {m.id}
                                            </span>
                                            <span className="text-white truncate">
                                                {m.nom}
                                            </span>
                                            <span
                                                className={
                                                    match && filter !== ""
                                                        ? "text-indigo-300"
                                                        : "text-gray-300"
                                                }
                                            >
                                                {m.ville}
                                            </span>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Php_db_caption")}
            </p>
        </section>
    );
}
